// /frontend/src/services/employee.api.ts

/**
 * API service for Employee-related endpoints.
 *
 * @author Alexander Funke
 * @lastChange 2026-04-07
 */

import { useApi } from './api.service';
import type { AxiosResponse } from 'axios';
import type {
  IEmployee,
  EmployeeCreateDto,
  EmployeeUpdateDto,
  PasswordUpdateDto,
  IMessageResponse,
} from '@shared/interfaces';

export type {
  IEmployee,
  EmployeeCreateDto,
  EmployeeUpdateDto,
  PasswordUpdateDto,
  IMessageResponse,
};

const BASE_URL = '/employees';

// --- API Functions ---

/** INDEX */
export const readEmployees = (): Promise<AxiosResponse<IEmployee[]>> => {
  return useApi().get<IEmployee[]>(BASE_URL);
}
/** CREATE */
export const createEmployee = (payload: EmployeeCreateDto): Promise<AxiosResponse<IEmployee>> => {
  return useApi().post(BASE_URL, payload);
}
/** READ */
export const readEmployee = (ID_employees: string): Promise<AxiosResponse<IEmployee>> => {
  return useApi().get<IEmployee>(`${BASE_URL}/${ID_employees}`);
}
/** UPDATE */
export const updateEmployee = (ID_employees: string, payload: EmployeeUpdateDto): Promise<AxiosResponse<IEmployee>> => {
  return useApi().patch<IEmployee>(`${BASE_URL}/${ID_employees}`, payload);
}
/** DELETE */
export const deleteEmployee = (ID_employees: string): Promise<AxiosResponse<void>> => {
  return useApi().delete<void>(`${BASE_URL}/${ID_employees}`);
}

/**
 * Updates the password for a specific employee (Admin action).
 *
 * @param ID_employees The UUID of the employee.
 * @param payload The new password data.
 * @returns A promise that resolves when the password is updated.
 *
 * @author  Alexander Funke
 * @lastChange 2026-04-07
 */
export const updateEmployeePassword = (ID_employees: string, payload: PasswordUpdateDto): Promise<AxiosResponse<IMessageResponse>> => {
  return useApi().patch<IMessageResponse>(`${BASE_URL}/${ID_employees}/password`, payload);
}
/**
 * Disables Two-Factor Authentication (TOTP) for a specific user (Admin action).
 *
 * @param ID_employees The UUID of the employee.
 * @returns A promise resolving to the updated employee profile.
 *
 * @author  Alexander Funke
 * @lastChange 2026-04-07
 */
export const disableTotpForUser = (ID_employees: string): Promise<AxiosResponse<IEmployee>> => {
  return useApi().post<IEmployee>(`${BASE_URL}/${ID_employees}/disable-totp`);
}
/**
 * Retrieves all employees who are NOT members of the specified company.
 * Useful for assigning new employees to a company.
 *
 * @param ID_companies The UUID of the company to exclude.
 * @returns A promise resolving to an array of eligible employees.
 *
 * @author  Alexander Funke
 * @lastChange 2026-04-07
 */
export const getEmployeesNotInCompany = (ID_companies: string): Promise<AxiosResponse<IEmployee[]>> => {
  return useApi().get<IEmployee[]>(`${BASE_URL}/not-in-company/${ID_companies}`);
}

/**
 * Fetches the profile of the currently authenticated user.
 *
 * @returns A promise with the server's response containing the user data.
 *
 * @author  Alexander Funke
 * @lastChange 2026-04-15
 */
export const getMyProfile = (): Promise<AxiosResponse<IEmployee>> => {
  return useApi().get<IEmployee>(`${BASE_URL}/me`);
}
